import { SearchActionTypes } from '@bridebook/web/lib/search/action-types';
import { WeddingWebsiteStorage } from '@bridebook/web/lib/storage-manager/basic/wedding-website';
import { clickedAnalytics } from '@bridebook/web/lib/ui/analytics';
import { WeddingActionTypes } from '@bridebook/web/lib/weddings/action-types';
import {
  IGenericWeddingWebsiteAnalytics,
  IInteractedWithWeddingWebsiteAnalytics,
  IInteractedWithWeddingWebsiteThemeGeneratorAIAnalytics,
  SavedWeddingWebsiteAnalytics,
  SectionConfig,
  WeddingWebsiteAnalyticEvents,
  weddingWebsiteAnalyticsPrefix,
  WeddingWebsiteAnalyticsTypes,
  WeddingWebsiteOnboardingAction,
} from './action-types';

export const weddingWebsiteOnboardingAnalytics = (
  location: string,
): WeddingWebsiteOnboardingAction => ({
  type: WeddingWebsiteAnalyticsTypes.FEATURE_ONBOARDING_STARTED,
  payload: {
    category: 'weddingWebsites',
    location,
  },
});

export const STARTED_ONBOARDING_SECTIONS: Record<string, string> = {
  details: 'weddingDetails',
  design: 'websiteDesign',
  security: 'privacyAndAccess',
  rsvp: 'rsvp',
  guestlist: 'guestlist',
  banner: 'banner',
};

export const SECTION_CONFIGS: Record<string, SectionConfig> = {
  details: {
    section: 'weddingWebsites_details',
    cta: 'Wedding details',
  },
  design: {
    section: 'weddingWebsites_design',
    cta: 'Website design',
  },
  security: {
    section: 'weddingWebsites_security',
    cta: 'Privacy & Access',
  },
  rsvp: {
    section: 'weddingWebsites_rsvp',
    cta: 'Set up RSVP',
  },
  guestlist: {
    section: 'weddingWebsites_guestlist',
    cta: 'Guest list',
  },
  invitesAndPaper: {
    section: 'weddingWebsites_paper&Invites',
    cta: 'Invites & Paper',
  },
};

export const weddingWebsiteSectionClickAnalytics = (
  sectionKey: keyof typeof SECTION_CONFIGS,
  onboarding?: boolean,
) => {
  if (onboarding) {
    const location = STARTED_ONBOARDING_SECTIONS[sectionKey];
    return weddingWebsiteOnboardingAnalytics(location);
  }

  const config = SECTION_CONFIGS[sectionKey];
  return clickedAnalytics({
    category: 'weddingWebsites',
    clickedLocation: 'weddingWebsitesPage',
    clickedSection: config.section,
    cta: config.cta,
  });
};

export const interactedWithWeddingWebsiteAnalytics = (
  payload: IInteractedWithWeddingWebsiteAnalytics,
) => ({
  type: WeddingWebsiteAnalyticsTypes.INTERACTED_WITH_WEDDING_WEBSITE,
  payload: {
    event: 'Interacted with wedding website',
    ...payload,
  },
});

export const genericWeddingWebsiteAnalytics = (payload: IGenericWeddingWebsiteAnalytics) => ({
  type: `${weddingWebsiteAnalyticsPrefix} ${payload.event}`,
  payload,
});

export const interactedWithWeddingWebsiteAnalyticsButtonClick = (
  payload: Pick<IInteractedWithWeddingWebsiteAnalytics, 'section' | 'element'>,
) =>
  interactedWithWeddingWebsiteAnalytics({
    action: 'clicked',
    location: 'preview',
    category: 'weddingWebsites',
    ...payload,
  });

export const trackPerformedSupplierSearchByName = ({
  searchedByNameTerm,
}: {
  searchedByNameTerm: string;
}) => ({
  type: WeddingWebsiteAnalyticsTypes.PERFORMED_SUPPLIER_SEARCH_BY_NAME,
  payload: {
    event: SearchActionTypes.FETCH_SEARCH_SUGGESTIONS_ANALYTICS,
    searchedByNameLocation: 'weddingWebsites_onboarding',
    searchedByNameTerm,
    pathname: '/website',
  },
});

export const editedVenueAction = () => ({
  type: 'EDITED_WEDDING_DETAILS_ANALYTICS',
  payload: { name: 'weddingLocation', weddingDetailsLocation: 'weddingWebsites_onboarding' },
});

export const trackEditedMainVenue = () => ({
  type: 'EDITED_WEDDING_DETAILS_ANALYTICS',
  payload: { name: 'weddingLocation', weddingDetailsLocation: 'weddingWebsites_details' },
});

export const startedCollaboration = () => ({
  type: 'EDITED_WEDDING_DETAILS_ANALYTICS',
  payload: { name: 'weddingLocation', weddingDetailsLocation: 'weddingWebsites_onboarding' },
});

export const clickedNotSureYet = (section: 'date' | 'weddingLocation') =>
  clickedAnalytics({
    category: 'weddingWebsites',
    clickedLocation: 'weddingWebsites_onboarding',
    clickedSection: section,
    cta: 'Not sure yet',
  });

export const trackSelectedWebsiteTheme = (palette: string, font: string) =>
  interactedWithWeddingWebsiteAnalytics({
    action: 'edited',
    location: 'onboarding',
    section: 'theme',
    element: 'preset',
    elementLabel: {
      font,
      palette,
    },
  });

export const trackUpdatedWebsitePhotos = (photoUrl: string, action: 'added' | 'deleted') =>
  interactedWithWeddingWebsiteAnalytics({
    action,
    location: 'onboarding',
    section: 'photos',
    element: photoUrl,
  });

export const trackCompletedOnboardingStep = (stepNumber: number) => {
  const stepContent = ['design', 'weddingDetails', 'photos'];

  return {
    type: WeddingWebsiteAnalyticsTypes.FEATURE_ONBOARDING_STEP_COMPLETED,
    payload: {
      onboardingStepNumber: stepNumber + 1,
      onboardingStepContent: stepContent[stepNumber],
      category: 'weddingWebsites',
    },
  };
};

export const trackFinishedOnboarding = () => ({
  type: WeddingWebsiteAnalyticsTypes.FEATURE_ONBOARDING_FINISHED,
  payload: {
    category: 'weddingWebsites',
  },
});

export const trackWeddingVenueUpdate = (supplier: string) => ({
  type: WeddingActionTypes.UPDATE_WEDDING_FIELD_SUCCESS,
  payload: {
    event: SearchActionTypes.FETCH_SEARCH_SUGGESTIONS_ANALYTICS,
    searchedByNameLocation: 'weddingWebsites_onboarding',
    searchedByNameTerm: supplier,
    pathname: '/website',
  },
});

export const trackSavedWeddingWebsite = ({
  location,
  section,
  cta,
  eventsVisible,
  faqVisible,
  giftListVisible,
  ourStoryVisible,
  placesToStayVisible,
  rsvpVisible,
  additionalSections,
  venuePhotos,
  sectionsOrderDict,
  defaultOrderingFlag,
  themeGeneratedAi,
}: Omit<SavedWeddingWebsiteAnalytics, 'event' | 'category'>) => {
  const clickedSection = WeddingWebsiteStorage.get()?.clickedSection;
  if (clickedSection) {
    if (!section || section === 'saveButton') {
      section = clickedSection;
    }
    WeddingWebsiteStorage.set({ clickedSection: undefined });
  }

  return {
    type: WeddingWebsiteAnalyticsTypes.SAVED_WEDDING_WEBSITE,
    payload: {
      event: WeddingWebsiteAnalyticEvents.SAVED_WEDDING_WEBSITE,
      location,
      section,
      cta,
      eventsVisible,
      faqVisible,
      giftListVisible,
      ourStoryVisible,
      placesToStayVisible,
      rsvpVisible,
      additionalSections,
      venuePhotos,
      sectionsOrderDict,
      defaultOrderingFlag,
      category: 'weddingWebsites',
      themeGeneratedAi,
    } satisfies SavedWeddingWebsiteAnalytics,
  };
};

export const interactedWithWeddingWebsiteThemeGeneratorAI = (
  payload: IInteractedWithWeddingWebsiteThemeGeneratorAIAnalytics,
) => ({
  type: WeddingWebsiteAnalyticsTypes.INTERACTED_WITH_WEDDING_WEBSITE_THEME_GENERATOR_AI,
  payload: {
    event: WeddingWebsiteAnalyticEvents.INTERACTED_WITH_WEDDING_WEBSITE_THEME_GENERATOR_AI,
    ...payload,
  },
});
